// client/src/utils/permissions.js - PRODUCTION READY VERSION

import { useState, useEffect } from 'react';
import { api } from './apiClient';
import { PERMISSION_CLIENT_CACHE_MS } from '../constants/time';

// ============= PRODUCTION READY PERMISSION CACHING =============
const permissionCache = new Map();
const CACHE_DURATION = PERMISSION_CLIENT_CACHE_MS;

export const clearPermissionCache = () => {
  permissionCache.clear();
};

// ============= CORE PERMISSION FUNCTIONS =============

/**
 * Check if user has a specific permission
 * @param {object} user - User object with permissions array from server
 * @param {string} permission - Permission key to check
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (user, permission) => {
  if (!user || !permission) return false;

  try {
    // Server sends flat permissions array - just check if permission exists
    const userPermissions = user.permissions || [];
    // SUPER_ADMIN bypasses all permission checks
    if (userPermissions.includes('SUPER_ADMIN')) return true;
    return userPermissions.includes(permission.toUpperCase());
  } catch (error) {
    return false;
  }
};

/**
 * Check if user has any of the specified permissions
 * @param {object} user - User object with permissions array
 * @param {string[]} permissions - Array of permission keys
 * @returns {boolean} True if user has at least one permission
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user || !Array.isArray(permissions)) return false;

  try {
    const userPermissions = user.permissions || [];
    if (userPermissions.includes('SUPER_ADMIN')) return true;
    return permissions.some(permission =>
      userPermissions.includes(permission.toUpperCase())
    );
  } catch (error) {
    return false;
  }
};

/**
 * Check if user has all of the specified permissions
 * @param {object} user - User object with permissions array
 * @param {string[]} permissions - Array of permission keys
 * @returns {boolean} True if user has all permissions
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user || !Array.isArray(permissions)) return false;

  try {
    const userPermissions = user.permissions || [];
    if (userPermissions.includes('SUPER_ADMIN')) return true;
    return permissions.every(permission =>
      userPermissions.includes(permission.toUpperCase())
    );
  } catch (error) {
    return false;
  }
};

// ============= USER PERMISSION FETCHING =============

/**
 * Fetch current user's permissions from server with caching
 * @returns {Promise<string[]>} Array of permission keys
 */
export const fetchUserPermissions = async () => {
  const cacheKey = 'current_user_permissions';
  const cached = permissionCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.permissions;
  }

  try {
    const response = await api.auth.me();
    // Handle both response.data.user and response.user structures
    const permissions = response.data?.user?.permissions || response.user?.permissions || [];
    
    // Cache the results
    permissionCache.set(cacheKey, {
      permissions,
      timestamp: Date.now()
    });
    
    return permissions;
  } catch (error) {
    return [];
  }
};

/**
 * React hook for user permissions with error handling
 * @returns {object} { permissions, loading, error, refetch, hasPermission, hasAnyPermission, hasAllPermissions }
 */
export const useUserPermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const userPermissions = await fetchUserPermissions();
      setPermissions(userPermissions);
    } catch (err) {
      setError(err.message);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const refetch = () => {
    clearPermissionCache();
    fetchPermissions();
  };

  return {
    permissions,
    loading,
    error,
    refetch,
    hasPermission: (permission) => permissions.includes('SUPER_ADMIN') || permissions.includes(permission.toUpperCase()),
    hasAnyPermission: (permissionList) => hasAnyPermission({ permissions }, permissionList),
    hasAllPermissions: (permissionList) => hasAllPermissions({ permissions }, permissionList)
  };
};

// ============= REACT COMPONENT PROTECTION =============

/**
 * Component wrapper for permission-based rendering with error boundaries
 */
export const ProtectedComponent = ({ 
  user, 
  permission, 
  permissions = [], 
  requireAll = false, 
  children, 
  fallback = null 
}) => {
  if (!user) {
    return fallback;
  }

  try {
    // Single permission check
    if (permission) {
      return hasPermission(user, permission) ? children : fallback;
    }

    // Multiple permissions check
    if (permissions.length > 0) {
      const allowed = requireAll 
        ? hasAllPermissions(user, permissions)
        : hasAnyPermission(user, permissions);
      return allowed ? children : fallback;
    }

    // No permission requirements
    return children;
  } catch (error) {
    return fallback;
  }
};

// ============= DISPLAY UTILITIES =============

/**
 * Format role key for display with proper error handling
 * @param {string|object} role - Role key or role object
 * @returns {string} Formatted role name
 */
export const formatRole = (role) => {
  if (!role) return 'No Role';
  
  try {
    // Handle both string and object cases
    let roleKey = typeof role === 'object' ? (role.key || role.name) : role;
    
    if (!roleKey || typeof roleKey !== 'string') return 'Unknown Role';
    
    return roleKey
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  } catch (error) {
    return 'Unknown Role';
  }
};

/**
 * Get user's primary role name for display with robust error handling
 * @param {object} user - User object
 * @returns {string} Role name for display
 */
export const getUserPrimaryRoleName = (user) => {
  if (!user) return 'No Role';
  
  try {
    // Handle primaryRole (can be string or object)
    if (user.primaryRole) {
      if (typeof user.primaryRole === 'object') {
        return user.primaryRole.name || user.primaryRole.key || 'Unknown Role';
      }
      return user.primaryRole;
    }
    
    // Handle dynamicRole fallback
    if (user.dynamicRole) {
      if (typeof user.dynamicRole === 'object') {
        return user.dynamicRole.name || user.dynamicRole.key || 'Unknown Role';
      }
      return user.dynamicRole;
    }
    
    // Legacy compatibility
    if (user.role) {
      if (typeof user.role === 'object') {
        return user.role.name || user.role.key || 'Unknown Role';
      }
      return formatRole(user.role);
    }
    
    return 'No Role Assigned';
  } catch (error) {
    return 'Unknown Role';
  }
};

/**
 * Get user's role status for display with comprehensive error handling
 * @param {object} user - User object
 * @returns {object} Role status information
 */
export const getUserRoleStatus = (user) => {
  if (!user) {
    return { status: 'no_user', message: 'No user' };
  }

  try {
    // Check for any role assignment
    const hasRole = user.primaryRole || user.dynamicRole || user.role;
    
    if (!hasRole) {
      return { status: 'unassigned', message: 'No role assigned' };
    }

    const primaryRole = getUserPrimaryRoleName(user);
    const additionalRoles = Array.isArray(user.additionalRoles) ? user.additionalRoles.length : 0;
    const totalPermissions = Array.isArray(user.permissions) ? user.permissions.length : 0;

    let message = primaryRole;
    if (additionalRoles > 0) {
      message += ` +${additionalRoles} additional`;
    }

    return {
      status: 'assigned',
      primaryRole,
      additionalRoles,
      totalPermissions,
      message
    };
  } catch (error) {
    return { status: 'error', message: 'Error determining role status' };
  }
};

// ============= RBAC ADMIN FUNCTIONS =============

/**
 * Fetch all available roles from server with error handling
 * @returns {Promise<object[]>} Array of role objects
 */
export const fetchAvailableRoles = async () => {
  try {
    const response = await api.rbac.roles.getAll({ isActive: true });
    return response.data?.roles || response.roles || [];
  } catch (error) {
    return [];
  }
};

/**
 * Fetch all available permissions from server (READ-ONLY)
 * @returns {Promise<object[]>} Array of permission objects
 */
export const fetchAvailablePermissions = async () => {
  try {
    const response = await api.rbac.permissions.getAll({ isActive: true });
    return response.data?.permissions || response.permissions || [];
  } catch (error) {
    return [];
  }
};

// ============= PRODUCTION RBAC API =============

export const rbacApi = {
  permissions: {
    getAll: (params = {}) => api.rbac.permissions.getAll(params)
  },
  
  roles: {
    getAll: (params = {}) => api.rbac.roles.getAll(params),
    getById: (id) => api.rbac.roles.getById(id),
    create: (data) => api.rbac.roles.create(data),
    update: (id, data) => api.rbac.roles.update(id, data),
    delete: (id) => api.rbac.roles.delete(id)
  },
  
  users: {
    getRoles: (userId) => api.rbac.users.getRoles(userId),
    assignRole: (userId, data) => api.rbac.users.assignRole(userId, data),
    removeRole: (userId, roleId) => api.rbac.users.removeRole(userId, roleId)
  }
};



// ============= PERFORMANCE UTILITIES =============

/**
 * Batch permission checks for better performance
 * @param {object} user - User object
 * @param {string[]} permissionList - List of permissions to check
 * @returns {object} Map of permission -> boolean
 */
export const batchPermissionCheck = (user, permissionList) => {
  if (!user || !Array.isArray(permissionList)) return {};

  try {
    const userPermissions = user.permissions || [];
    const isSuperAdmin = userPermissions.includes('SUPER_ADMIN');
    const results = {};

    permissionList.forEach(permission => {
      results[permission] = isSuperAdmin || userPermissions.includes(permission.toUpperCase());
    });

    return results;
  } catch (error) {
    return {};
  }
};

/**
 * Memoized permission checker for React components
 * @param {object} user - User object
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has permission
 */
export const memoizedHasPermission = (() => {
  const cache = new Map();
  
  return (user, permission) => {
    if (!user || !permission) return false;
    
    const cacheKey = `${user.id || 'unknown'}_${permission}`;
    const cached = cache.get(cacheKey);
    
    if (cached !== undefined) {
      return cached;
    }
    
    const result = hasPermission(user, permission);
    cache.set(cacheKey, result);
    
    // Clear cache after 1 minute
    setTimeout(() => cache.delete(cacheKey), 60000);
    
    return result;
  };
})();

// ============= EXPORTS =============

const PermissionsSystem = {
  // Core permission functions
  hasPermission,
  hasAnyPermission, 
  hasAllPermissions,
  
  // React hooks and components
  useUserPermissions,
  ProtectedComponent,
  
  // Fetching functions
  fetchUserPermissions,
  fetchAvailableRoles,
  fetchAvailablePermissions,
  
  // Display utilities
  formatRole,
  getUserPrimaryRoleName,
  getUserRoleStatus,
  
  // Cache management
  clearPermissionCache,
  
  // Performance utilities
  batchPermissionCheck,
  memoizedHasPermission,
  
  // RBAC API
  rbacApi
};

export default PermissionsSystem;